import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Icon, Paper, Snackbar, Tab, Tabs, Tooltip } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridColumnVisibilityModel, GridRenderCellParams } from '@mui/x-data-grid';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import PlannenActionMenu from './PlannenActionMenu';
import DialogConfirm from '../../controls/dialogs/DialogConfirm';
import MaStatusSwitch from '../../controls/MaStatusSwitch';
import { PortfolioPlan } from '../../../models/portfolio/portfolio';

import { helperService } from '../../../services/helper.service';
import { authService } from '../../../services/auth.service';
import { contentService } from '../../../services/content.service';
import { mapService } from '../../../services/map.service';
import { eventBus } from '../../../services/event-emitter.service';
import { PortfolioService, portfolioService } from '../../../services/portfolio.service';
import { InclusionRequestStatusEnum } from '../../../content/inclusionRequestStatus';
import { loadJsonFromLocalStorage, saveJsonToLocalStorage } from '../../../services/utils/localStorageState';
import { pageSizeStorageKey } from '../../../services/utils/localStorageKeys';
import { usePersistedPaginationModel } from '../../../hooks/usePersistedPageSize';


interface OverzichtPlannenProps {
  plannen?: PortfolioPlan[];
  activeParticipant?: boolean;
  mijnOrganisatieId?: string;
  canCrud?: boolean;
  refreshKey?: number;
}

const LS_VISIBLE_COLUMNS_KEY_ACTIEF = 'portfolio-overzichtplannen-visible-columns-actief-v1';
const LS_VISIBLE_COLUMNS_KEY_ARCHIEF = 'portfolio-overzichtplannen-visible-columns-archief-v1';
const LS_PAGE_SIZE_KEY_ACTIEF = pageSizeStorageKey('portfolio.plannen.actief');
const LS_PAGE_SIZE_KEY_ARCHIEF = pageSizeStorageKey('portfolio.plannen.archief');

type PlanTabValue = 0 | 1;

const OverzichtPlannen: React.FC<OverzichtPlannenProps> = ({ 
  plannen,
  activeParticipant,
  mijnOrganisatieId,
  canCrud,
  refreshKey
}) => {
  const [statusOrderMap, setStatusOrderMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<PlanTabValue>(0);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [selectedAfstemmingPlan, setSelectedAfstemmingPlan] = useState<PortfolioPlan | null>(null);
  const [openConfirmAfstemming, setOpenConfirmAfstemming] = useState<boolean>(false);
  const currentStorageKey = tabValue === 0 ? LS_VISIBLE_COLUMNS_KEY_ACTIEF : LS_VISIBLE_COLUMNS_KEY_ARCHIEF;
  const currentPageSizeKey = tabValue === 0 ? LS_PAGE_SIZE_KEY_ACTIEF : LS_PAGE_SIZE_KEY_ARCHIEF;
  const hasCrudRights = canCrud ?? authService.canCrud();
  const { paginationModel, handlePaginationModelChange } = usePersistedPaginationModel(currentPageSizeKey);

  const canTogglePlanAfstemming = useCallback((plan: PortfolioPlan) => {
    const status = (plan.planAfstemmingsStatus ?? '').toLowerCase();
    const isToggleStatus = status === 'in_afstemming' || status === 'afgestemd';
    const isAcceptedPlan = (plan.inclusionRequestStatus ?? '').toLowerCase() === InclusionRequestStatusEnum.Accepted;
    const isPlanOwner = plan.organizationId === mijnOrganisatieId;

    return !!hasCrudRights && isPlanOwner && isAcceptedPlan && isToggleStatus;
  }, [hasCrudRights, mijnOrganisatieId]);

  const getNextAfstemmingStatus = useCallback((currentStatus?: string | null) => {
    return (currentStatus ?? '').toLowerCase() === 'afgestemd' ? 'in_afstemming' : 'afgestemd';
  }, []);

  const getAfstemmingConfirmText = useCallback((plan: PortfolioPlan) => {
    const nextStatus = getNextAfstemmingStatus(plan.planAfstemmingsStatus);
    const isToAfgestemd = nextStatus === 'afgestemd';

    return {
      title: isToAfgestemd ? 'Plan op afgestemd zetten' : 'Plan terug naar in afstemming zetten',
      content: isToAfgestemd
        ? `Weet je zeker dat je plan ${plan.planNaam} op afgestemd wilt zetten?`
        : `Weet je zeker dat je plan ${plan.planNaam} terug naar in afstemming wilt zetten?`,
      button: 'Ja dat weet ik zeker',
      nextStatus,
    };
  }, [getNextAfstemmingStatus]);

  const requestToggleAfstemming = useCallback((plan: PortfolioPlan) => {
    if (!canTogglePlanAfstemming(plan)) {
      return;
    }

    setSelectedAfstemmingPlan(plan);
    setOpenConfirmAfstemming(true);
  }, [canTogglePlanAfstemming]);

  const handleConfirmToggleAfstemming = useCallback(async () => {
    if (!selectedAfstemmingPlan?.inclusionRequestId) {
      setOpenConfirmAfstemming(false);
      setSelectedAfstemmingPlan(null);
      return;
    }

    const { nextStatus } = getAfstemmingConfirmText(selectedAfstemmingPlan);

    try {
      setLoading(true);
      await portfolioService.updatePlanAfstemmingStatus(nextStatus, selectedAfstemmingPlan.inclusionRequestId);

      if (nextStatus === 'in_afstemming') {
        const planNaam = selectedAfstemmingPlan.planNaam || 'onbekend plan';
        eventBus.emit('openAnnotationPrefill', {
          title: `${planNaam} terug naar in afstemming`,
          description: `Notitie: Ik heb de afstemmingsstatus van plan ${planNaam} weer naar ‘in afstemming’ gezet omdat...`,
        });
        eventBus.emit('openDialog', 'annotation');
      }

      setSnackbarMessage(
        nextStatus === 'afgestemd'
          ? 'Plan is op afgestemd gezet'
          : 'Plan is teruggezet naar in afstemming'
      );
      setOpenSnackbar(true);

      setTimeout(() => {
        eventBus.emit('reloadPortfolio', nextStatus);
      }, 4000);
    }
    catch (err: any) {
      setSnackbarMessage(err?.message || 'Er is een fout opgetreden bij wijzigen van de afstemmingsstatus');
      setOpenSnackbar(true);
    }
    finally {
      setLoading(false);
      setOpenConfirmAfstemming(false);
      setSelectedAfstemmingPlan(null);
    }
  }, [getAfstemmingConfirmText, selectedAfstemmingPlan]);

  const actuelePlannen = useMemo(() => {
    return (plannen ?? []).filter((plan) => {
      const status = (plan.inclusionRequestStatus ?? '').toLowerCase();
      return status === InclusionRequestStatusEnum.Accepted || status === InclusionRequestStatusEnum.Pending;
    });
  }, [plannen]);

  const archiefPlannen = useMemo(() => {
    return (plannen ?? []).filter((plan) => {
      const status = (plan.inclusionRequestStatus ?? '').toLowerCase();
      return status !== InclusionRequestStatusEnum.Accepted && status !== InclusionRequestStatusEnum.Pending;
    });
  }, [plannen]);

  const rows = tabValue === 0 ? actuelePlannen : archiefPlannen;

  const gridColumns = useMemo<(GridColDef<PortfolioPlan> | GridActionsColDef<PortfolioPlan>)[]>(() => {
    const afstemmingsColumn: GridColDef<PortfolioPlan>[] = tabValue === 0 ? [{
      field: 'planAfstemmingsStatus',
      headerName: 'Afstemming',
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<PortfolioPlan>) => {
        const row = params.row as PortfolioPlan;
        const status = (row as any).planAfstemmingsStatus || '';
        const statusOption = PortfolioService.STATUS_OPTIONS.find(option => option.id === status);
        const isAfgestemd = status === 'afgestemd';
        const canToggle = canTogglePlanAfstemming(row);
        const tooltipText = statusOption ? `Afstemmingsstatus: ${statusOption.name}` : 'Geen afstemmingsstatus beschikbaar';

        if (!statusOption || statusOption.id === 'nieuw') {
          return <span></span>;
        }

        return (
          <Tooltip title={canToggle ? tooltipText : `${tooltipText} (niet wijzigbaar)`} placement="right" arrow>
            <span>
              <MaStatusSwitch
                checked={isAfgestemd}
                disabled={!canToggle}
                offIcon="handshake"
                onIcon="handshake"
                offThumbColor="#fff0d9"
                onThumbColor="#e4f3e5"
                offIconColor="#ff9800"
                onIconColor="#4caf50"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  event.stopPropagation();
                  requestToggleAfstemming(row);
                }}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => event.stopPropagation()}
              />
            </span>
          </Tooltip>
        );
      },
    }] : [];

    return [
      {
        field: 'planNaam',
        headerName: 'Naam',
        flex: 3,
        renderCell: (params: any) => (
          <>
            {params.row.planId ? (
              <button
                type="button"
                className="table-link-button"
                onClick={() => onClickPlanLink(params.row)}
              >
                {params.row.planNaam}
              </button>
            ) : (
              <Tooltip title="Dit plan wordt niet met jouw organisatie gedeeld" placement="right" arrow>
                <span>Dit plan wordt niet met jouw organisatie gedeeld</span>
              </Tooltip>
            )}
          </>
        ),
      },
      ...afstemmingsColumn,
      {
        field: 'discipline',
        headerName: 'Discipline',
        flex: 0.5,
        renderCell: (params: any) => {
          const camelDiscipline = helperService.toCamelCase(params.row.discipline);
          return (
            <Tooltip title={params.row.discipline} placement="right" arrow>
              <span className={`category-icon category-icon-small ${camelDiscipline}-icon-white`}>
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'organizationName',
        headerName: 'Organisatie',
        flex: 1,
        renderCell: (params: any) => <>{params.row.organizationName}</>,
      },
      {
        field: 'planType',
        headerName: 'Type',
        flex: 1,
        renderCell: (params: any) => <>{params.row.planType}</>,
      },
      {
        field: 'planStatus',
        headerName: 'Status',
        flex: 1,
        renderCell: (params: any) => <>{params.row.planStatus}</>,
      },
      {
        field: 'planStartDatum',
        headerName: 'Start',
        flex: 1,
        renderCell: (params: any) => {
          const value = params.row.planStartDatum;
          if (!value) return '';
          const date = new Date(value);
          return isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
        }
      },
      {
        field: 'planEindDatum',
        headerName: 'Eind',
        flex: 1,
        renderCell: (params: any) => {
          const value = params.row.planEindDatum;
          if (!value) return '';
          const date = new Date(value);
          return isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
        }
      },
      {
        field: 'inclusionRequestStatus',
        headerName: 'Deelname',
        flex: 1,
        renderCell: (params: any) => <>{getInclusionRequestStatusLabel(params.row.inclusionRequestStatus)}</>,
        sortComparator: (a: string, b: string) => {
          const order1 = statusOrderMap[a] ?? Number.MAX_SAFE_INTEGER;
          const order2 = statusOrderMap[b] ?? Number.MAX_SAFE_INTEGER;
          return order1 - order2;
        }
      },
      {
        field: 'actions',
        headerName: 'Functies',
        renderHeader: () => <></>,
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<PortfolioPlan>) => (
          <>
            {params.row.planId && (
              <PlannenActionMenu
                params={params}
                activeParticipant={activeParticipant}
                mijnOrganisatieId={mijnOrganisatieId}
                canCrud={canCrud}
              />
            )}
          </>
        )
      }
    ];
  }, [activeParticipant, canCrud, canTogglePlanAfstemming, mijnOrganisatieId, requestToggleAfstemming, statusOrderMap, tabValue]);

  const allColumnFields = useMemo(() => gridColumns.map(col => col.field), [gridColumns]);
  const defaultVisibleColumns = useMemo(() => allColumnFields, [allColumnFields]);

  const columnVisibilityModel = useMemo(() => {
    return Object.fromEntries(allColumnFields.map((field) => [field, visibleColumns.includes(field)]));
  }, [allColumnFields, visibleColumns]);

  const handleColumnVisibilityChange = useCallback((model: GridColumnVisibilityModel) => {
    const nextVisibleColumns = allColumnFields.filter((field) => model[field] !== false);
    setVisibleColumns(nextVisibleColumns);
    saveJsonToLocalStorage(currentStorageKey, nextVisibleColumns);
  }, [allColumnFields, currentStorageKey]);

  useEffect(() => {
    const statusses = contentService.getInclusionRequestStatusses();
    const orderMap = statusses.reduce((acc: Record<string, number>, status: any) => {
      acc[status.value] = status.order;
      return acc;
    }, {});
    setStatusOrderMap(orderMap);
  }, []);

  useEffect(() => {
    setLoading(!plannen);
  }, [plannen, refreshKey]);

  useEffect(() => {
    const normalizedColumns = (value: any): string[] => {
      if (!Array.isArray(value)) {
        return defaultVisibleColumns;
      }

      const nextColumns = value.filter((field: unknown): field is string =>
        typeof field === 'string' && allColumnFields.includes(field)
      );

      if (nextColumns.length === 0) {
        return defaultVisibleColumns;
      }

      return nextColumns;
    };

    const storedVisibleColumns = loadJsonFromLocalStorage<string[]>(
      currentStorageKey,
      defaultVisibleColumns,
      normalizedColumns
    );

    setVisibleColumns(storedVisibleColumns);
  }, [allColumnFields, currentStorageKey, defaultVisibleColumns]);

  const onClickPlanLink = (plan: PortfolioPlan) => {
    if (!mapService.isValidBbox(plan?.bbox)) {
      setSnackbarMessage('De begrenzing van dit plan is ongeldig of onvolledig');
      setOpenSnackbar(true);
      return;
    }

    eventBus.emit('selectPlan', plan.infplId ?? null);
    eventBus.emit('zoomInOnPlanBbox', plan.bbox);
  };

  const getInclusionRequestStatusLabel = (id: string) => {
    const statusLabel = contentService.getInclusionRequestStatusLabel(id);
    return statusLabel ?? id; // fallback to id if label not found
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key="topcenter"
      />

      <Tabs
        value={tabValue}
        onChange={(_e, value: PlanTabValue) => setTabValue(value)}
        aria-label="plannen tabs"
        TabIndicatorProps={{ className: `indicator-${tabValue}` }}
        sx={{ overflow: 'visible', '& .MuiTabs-flexContainer': { overflow: 'visible' } }}
      >
        <Tab 
          value={0} 
          label={(
            <Badge badgeContent={actuelePlannen.length} color="primary" overlap="rectangular" sx={{ '& .MuiBadge-badge': { right: -14, top: 2 } }}>
              <span>Plannen</span>
            </Badge>
          )} 
          icon={<Icon className={`icon-cirle pending`}>map</Icon>}
          iconPosition="start"
          className="pending"
          sx={{ overflow: 'visible' }}
        />
        <Tab 
          value={1} 
          label={(
            <Badge badgeContent={archiefPlannen.length} color="primary" overlap="rectangular" sx={{ '& .MuiBadge-badge': { right: -14, top: 2 } }}>
              <span>Archief</span>
            </Badge>
          )} 
          icon={<Icon className={`icon-cirle ongoing`}>archive</Icon>}
          iconPosition="start"
          className="ongoing"
          sx={{ overflow: 'visible' }}
        />
      </Tabs>

      <div className="grid-responsive">
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={gridColumns}
            getRowId={(row) => row.infplId || ''}
            getRowClassName={(params) => `discipline-row ${helperService.toCamelCase(params.row.discipline)}-border`}
            localeText={{
              noRowsLabel: 'Geen plannen',
              MuiTablePagination: {
                labelRowsPerPage: 'Plannen per pagina:',
                labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) => `${from}-${to} van ${count}`,
              },
            }}
            disableColumnFilter
            disableRowSelectionOnClick
            sx={{ border: 0 }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            pagination
            pageSizeOptions={[5, 10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            initialState={{
              sorting: {
                sortModel: [{ field: 'inclusionRequestStatus', sort: 'asc' }],
              },
            }}
          />
        </Paper>
      </div>

      <DialogConfirm
        open={openConfirmAfstemming}
        title={selectedAfstemmingPlan ? getAfstemmingConfirmText(selectedAfstemmingPlan).title : 'Afstemmingsstatus aanpassen'}
        contentText={selectedAfstemmingPlan ? getAfstemmingConfirmText(selectedAfstemmingPlan).content : 'Weet je zeker dat je deze status wilt aanpassen?'}
        confirmButtonText={selectedAfstemmingPlan ? getAfstemmingConfirmText(selectedAfstemmingPlan).button : 'Ja dat weet ik zeker'}
        onClose={() => {
          setOpenConfirmAfstemming(false);
          setSelectedAfstemmingPlan(null);
        }}
        onConfirm={handleConfirmToggleAfstemming}
      />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default OverzichtPlannen;
